// src/components/NodeApplication.jsx

import React, { useState, useEffect } from 'react';
import NodeTierCard from './NodeTierCard';
import NodeApplicationForm from './NodeApplicationForm';
import NodeApplicationList from './NodeApplicationList';
import ApplicationStatusCheck from './ApplicationStatusCheck';
import { useWallet } from '../context/WalletContext';
import * as api from '../services/api';
import '../styles/node-application.css';
import '../styles/node-application-loading.css';

const NodeApplication = () => {
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedTierAfiValue, setSelectedTierAfiValue] = useState(null);
  const [nodeTiers, setNodeTiers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { wallet, openWalletModal } = useWallet();
  
  // Fetch node tiers from API with retry logic and improved error handling
  useEffect(() => {
    let isMounted = true;
    
    const fetchNodeTiers = async (retryCount = 0) => {
      try {
        setIsLoading(true);
        
        // Use the fallback data immediately to prevent UI issues
        const fallbackTiers = [
          {
            id: 'standard',
            name: 'Standard Node',
            level: 1,
            stakingRequirement: '100,000 AFI',
            lockPeriod: '60 days',
            maxDevices: 75,
            miningRewards: '10%',
            salesCommission: '7%',
            superiorRewards: '3%',
            votingWeight: '1.0×',
            responsibilities: [
              'Maintain device uptime',
              'Guide new users',
              'Promote the ecosystem'
            ],
            adjustmentFactor: 1.0,
          },
          {
            id: 'super',
            name: 'Super Node',
            level: 2,
            stakingRequirement: '250,000 AFI',
            lockPeriod: '90 days',
            maxDevices: 250,
            miningRewards: '13%',
            salesCommission: '10%',
            superiorRewards: '4%',
            votingWeight: '1.2×',
            responsibilities: [
              'Team management',
              'Training Level 1 nodes',
              'Monitoring device performance',
              'Community proposal voting'
            ],
            adjustmentFactor: 1.0,
          },
          {
            id: 'master',
            name: 'Master Node',
            level: 3,
            stakingRequirement: '500,000 AFI',
            lockPeriod: '120 days',
            maxDevices: 600,
            miningRewards: '16%',
            salesCommission: '13%',
            superiorRewards: '5%',
            votingWeight: '1.5×',
            responsibilities: [
              'Regional market development',
              'Team training',
              'Network governance participation',
              'Can submit improvement proposals'
            ],
            adjustmentFactor: 1.0,
          },
          {
            id: 'apex',
            name: 'Apex Node',
            level: 4,
            stakingRequirement: '1,000,000 AFI',
            lockPeriod: '180 days',
            maxDevices: 'Unlimited',
            miningRewards: '18%',
            salesCommission: '15%',
            superiorRewards: '6%',
            votingWeight: '2.0×',
            responsibilities: [
              'Leading large teams',
              'Developing regional strategies',
              'Driving ecosystem growth',
              'Participation in major decision voting',
              'Registration fee reward pool share'
            ],
            adjustmentFactor: 1.0,
          }
        ];
        
        // Try to fetch from API in the background
        try {
          const response = await api.fetchNodeTiers();
          
          if (!isMounted) return;
          
          // Process API response if valid
          if (response && Array.isArray(response)) {
            // API returned array directly
            const formattedTiers = response.map(tier => ({
              id: tier.id || '',
              name: tier.name || 'Unknown Tier',
              level: tier.level || 0,
              stakingRequirement: tier.staking_requirement || '0 AFI',
              lockPeriod: tier.lock_period || '0 days',
              maxDevices: tier.max_devices || 0,
              miningRewards: tier.mining_rewards || '0%',
              salesCommission: tier.sales_commission || '0%',
              superiorRewards: tier.superior_rewards || '0%',
              votingWeight: tier.voting_weight || '0×',
              adjustmentFactor: tier.adjustment_factor || 1.0,
              responsibilities: Array.isArray(tier.responsibilities) ? tier.responsibilities : []
            }));
            
            setNodeTiers(formattedTiers);
            setError(null);
          } else if (response && response.data && Array.isArray(response.data)) {
            // API returned { data: [...] }
            const formattedTiers = response.data.map(tier => ({
              id: tier.id || '',
              name: tier.name || 'Unknown Tier',
              level: tier.level || 0,
              stakingRequirement: tier.staking_requirement || '0 AFI',
              lockPeriod: tier.lock_period || '0 days',
              maxDevices: tier.max_devices || 0,
              miningRewards: tier.mining_rewards || '0%',
              salesCommission: tier.sales_commission || '0%',
              superiorRewards: tier.superior_rewards || '0%',
              votingWeight: tier.voting_weight || '0×',
              adjustmentFactor: tier.adjustment_factor || 1.0,
              responsibilities: Array.isArray(tier.responsibilities) ? tier.responsibilities : []
            }));
            
            setNodeTiers(formattedTiers);
            setError(null);
          } else {
            // Invalid response format, keep using fallback data
            console.warn('Unexpected API response format, using fallback data');
            setNodeTiers(fallbackTiers);
          }
        } catch (apiError) {
          console.error(`Failed to fetch node tiers (attempt ${retryCount + 1}):`, apiError);
          
          if (!isMounted) return;
          
          // Use fallback data on error
          setNodeTiers(fallbackTiers);
          
          // Retry up to 2 times with increasing delay
          if (retryCount < 2) {
            const retryDelay = 1000 * (retryCount + 1); // Exponential backoff
            setTimeout(() => fetchNodeTiers(retryCount + 1), retryDelay);
          }
        }
        
        // Always set loading to false after attempting API call
        setIsLoading(false);
        
      } catch (err) {
        console.error(`Error in fetchNodeTiers:`, err);
        
        if (!isMounted) return;
        
        // Set fallback data on any error
        setNodeTiers([
          {
            id: 'standard',
            name: 'Standard Node',
            level: 1,
            stakingRequirement: '100,000 AFI',
            lockPeriod: '60 days',
            maxDevices: 75,
            miningRewards: '10%',
            salesCommission: '7%',
            superiorRewards: '3%',
            votingWeight: '1.0×',
            responsibilities: [
              'Maintain device uptime',
              'Guide new users',
              'Promote the ecosystem'
            ],
            adjustmentFactor: 1.0,
          },
          // ... other tiers
        ]);
        setIsLoading(false);
      }
    };

    fetchNodeTiers();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSelectTier = (tierId, afiValue) => {
    if (wallet.connected) { // Only allow selection if wallet is connected
      setSelectedTier(tierId);
      setSelectedTierAfiValue(afiValue);
      
      // Scroll to application form
      setTimeout(() => {
        const formElement = document.getElementById('application-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Prompt for wallet connection
      openWalletModal();
    }
  };

  return (
    <section className="node-application" id="node-application">
      <div className="container">
        <h2 className="section-title">Become a <span>Team Leader</span></h2>
        <p className="section-subtitle">
          Apply to become an Aledger node operator and build your own team while earning stable long-term income through our decentralized node system.
        </p>
        
        {/* Application Status Check */}
        <ApplicationStatusCheck />
        
        {/* Show existing applications if wallet is connected */}
        {wallet.connected && <NodeApplicationList />}
        
        {isLoading ? (
          <div className="tiers-loading">
            <span className="spinner"></span>
            <p>Loading node tiers...</p>
          </div>
        ) : error ? (
          <div className="tiers-error">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
              <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>{error}</p>
          </div>
        ) : (
          <div className="node-tiers">
            {nodeTiers.map(tier => (
              <NodeTierCard 
                key={tier.id}
                tier={tier}
                isSelected={selectedTier === tier.id}
                onSelect={handleSelectTier}
              />
            ))}
          </div>
        )}
        
        <div className="adjustment-note">
          <div className="note-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="note-content">
            <h4>Dynamic Node Staking Requirements</h4>
            <p>
              As the AFI token price fluctuates, node staking requirements will dynamically adjust according to the formula:
              <span className="formula">Adjusted Node Staking = Initial Requirement × (Initial AFI Price / Current AFI Price) × Adjustment Factor</span>
              Where Initial AFI Price is 0.02 USDT, and Adjustment Factor ranges from 0.8 to 1.2.
            </p>
          </div>
        </div>
        
        <div id="application-form" className="application-form-section">
          <h3 className="form-title">Node Application Form</h3>
          <p className="form-description">
            Fill out the form below to apply for your selected node level. Our team will review your application and get back to you within 48 hours.
          </p>
          
          <NodeApplicationForm 
            selectedTier={selectedTier} 
            nodeTiers={nodeTiers} 
            selectedAfiValue={selectedTierAfiValue}
          />
        </div>
      </div>
    </section>
  );
};

export default NodeApplication;
